import { IGroup, INavLink } from "@fluentui/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { IntlShape } from "react-intl";
import ajax from "@cayo/shared/libs/ajax";
import logger from "@cayo/shared/libs/logger";
import globalHooks from "../GlobalHooks";
import TreeController from "./tree.controller";

const log = logger.getLogger("workflow");

type LoadTreeFunction = (updateTime: Date, rootUrl: string, selectedKey: string) => Promise<any>;

const memoizeTreeLoader = (fn: (param: string) => Promise<any>): LoadTreeFunction => {
  const cache: Record<string, any> = {};

  return async (updateTime: Date, rootUrl: string, selectedKey: string): Promise<any> => {
    const cacheKey = `${updateTime}-${rootUrl}`;
    log.debug(cacheKey);

    if (cache[cacheKey]) {
      return cache[cacheKey];
    }

    const tree = await fn(`${selectedKey}-${rootUrl}`);
    cache[cacheKey] = tree;

    return tree;
  };
};

const useTree = (
  rootUrl: string,
  refreshInterval: number,
  intl: IntlShape,
  onItemSelected: (item?: IGroup | INavLink) => void
) => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [groups, setGroups] = useState<IGroup[]>([]);
  const [items, setItems] = useState<INavLink[]>([]);
  const [selectedKey, setSelectedKey] = useState<string>();

  const { fetchFn } = ajax;

  const treeCtrl = useMemo(() => new TreeController(rootUrl, fetchFn, intl), [rootUrl]);
  const memoLoad = useMemo(() => memoizeTreeLoader(treeCtrl.loadTree), [treeCtrl]);

  const { updateTime } = globalHooks.useInterval(refreshInterval);

  useEffect(() => {
    //setIsLoading(false);

    const asyncLoad = async () => {
      log.debug(
        "loading Tree -> updateTime, rootUrl, selectedKey",
        updateTime,
        rootUrl,
        selectedKey
      );
      try {
        const tree = await memoLoad(updateTime, rootUrl || "", selectedKey || "");
        if (tree !== null && tree !== undefined) {
          setGroups(tree.groups);
          setItems(tree.items);
        } else {
          log.debug("skip loading");
        }
      } catch (e) {
        log.error(`Failed to load tree by url: ${rootUrl}`, e);
      } finally {
        setIsLoading(false);
      }
    };

    asyncLoad();
  }, [updateTime, rootUrl, selectedKey]);

  const selectItem = useCallback(
    (item: IGroup | INavLink | undefined) => {
      setSelectedKey(item?.key);
      onItemSelected(item);

      if (!groups.length) {
        setItems([...items]);
      }
    },
    [groups, items]
  );

  return { items, groups, isLoading, selectedKey, selectItem };
};

export default useTree;
